/* ============================================================================
 * Movatic WMS — Mobile Shell
 * Arquivo: src/mobile/components/MobileShell.tsx
 * Autor: CesarFilho 932048016
 * ----------------------------------------------------------------------------
 * Responsabilidade:
 *  - Compor o “shell” das telas móveis (header + conteúdo).
 *  - Exibir a moldura (PhoneFrame) somente no modo simulador (?sim=1).
 *  - Ocultar a barra de abas inferior em device real (para não duplicar
 *    navegação com os cards já exibidos na Home).
 *  - No device real, usar largura total da tela (sem max-w) para aproveitar
 *    melhor o espaço do coletor/smartphone.
 *
 * Regras:
 *  - Simulador: querystring ?sim=1 -> mostra PhoneFrame e mantém max-w
 *  - Device real: detecta por userAgent+display-mode; sem moldura e sem tabs
 *  - A querystring é preservada entre as rotas (útil para manter ?sim=1)
 *
 * Manutenção:
 *  - A deteccao de dispositivo vive em useDeviceMode (hooks/); ajustar la.
 *  - Para tabs no mobile, ajustar showBottomTabs no hook.
 * ========================================================================== */

import React from "react";
import { Link, useLocation } from "react-router-dom";
import PhoneFrame from "./PhoneFrame";
import { useDeviceMode } from "../hooks/useDeviceMode";

// Mantemos amplo para ícones (lucide-react etc.)
type IconComponent = React.ComponentType<any>;

type Props = {
  tabs: { to: string; label: string; icon: IconComponent }[];
  children: React.ReactNode;
};

/**
 * MobileShell
 * - Renderiza header + conteúdo + (tabs opcionais).
 * - Moldura PhoneFrame apenas quando simulador.
 * - Sem tabs no mobile real para não duplicar navegação.
 */
export default function MobileShell({ children, tabs }: Props) {
  const loc = useLocation();
  const { simulator, realMobile, showBottomTabs } = useDeviceMode();

  // Barra inferior apenas fora do mobile real
  const showTabs = showBottomTabs;

  // Classes de container: no simulador, centralizado e largura fixa;
  // no device real, usar largura total.
  const containerW =
    simulator ? "mx-auto max-w-[420px]" : "w-full max-w-none";

  // Espaço inferior do main: dar padding só quando há tabs fixas
  const mainBottomPad = showTabs ? "pb-20" : "pb-4";

  const currentPath = loc.pathname;

  const content = (
    <div className="coletor-root min-h-screen bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100">
      {/* ------------------------------------------------------------------
         Header compacto e consistente
         ------------------------------------------------------------------ */}
      <header className="sticky top-0 z-10 bg-white/80 dark:bg-neutral-900/80 backdrop-blur border-b border-neutral-200 dark:border-neutral-800">
        <div className={`${containerW} px-3 h-12 flex items-center justify-between`}>
          <div className="font-semibold">Movatic WMS • Coletor</div>
          <div className="text-xs opacity-60">PWA</div>
        </div>
      </header>

      {/* ------------------------------------------------------------------
         Conteúdo
         - No device real, ocupa a largura total (sem moldura/max-w).
         - Em simulador, mantém largura “de telefone” para visualização.
         ------------------------------------------------------------------ */}
      <main className={`${containerW} px-3 ${mainBottomPad}`}>{children}</main>

      {/* ------------------------------------------------------------------
         Barra de abas inferior
         - Oculta no device real para evitar navegação duplicada.
         - Visível no simulador/desktop (útil durante o DEV).
         ------------------------------------------------------------------ */}
      {showTabs && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 border-t border-neutral-200 dark:border-neutral-800">
          <div className={`${containerW} grid grid-cols-4 gap-1 p-1`}>
            {tabs.map((t) => {
              const active = currentPath.startsWith(t.to);
              const Icon = t.icon;
              return (
                <Link
                  key={t.to}
                  to={t.to + (loc.search || "")}
                  className={[
                    "group relative select-none rounded-xl text-[11px] font-medium transition-transform",
                    active ? "translate-y-[1px]" : "",
                  ].join(" ")}
                >
                  {/* “fundo” 3D */}
                  <div
                    className={[
                      "absolute inset-0 rounded-xl",
                      "bg-gradient-to-b",
                      active
                        ? "from-blue-600/85 to-blue-700/85"
                        : "from-neutral-200 to-neutral-300 dark:from-neutral-800 dark:to-neutral-700",
                      "shadow-[inset_0_1px_0_rgba(255,255,255,.25),0_1px_0_rgba(0,0,0,.15)]",
                    ].join(" ")}
                  />
                  {/* face do botão */}
                  <div
                    className={[
                      "relative z-10 flex flex-col items-center justify-center gap-0.5",
                      "h-14 rounded-xl",
                      active
                        ? "text-white shadow-[inset_0_1px_0_rgba(255,255,255,.25),0_6px_10px_rgba(0,0,0,.25)]"
                        : "text-neutral-700 dark:text-neutral-100 bg-gradient-to-b from-white to-neutral-100 dark:from-neutral-900 dark:to-neutral-800",
                    ].join(" ")}
                  >
                    <Icon size={18} className={active ? "" : "opacity-90"} />
                    <span className="leading-none">
                      <span className={active ? "text-white" : ""}>{t.label}</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );

  // PATCHED 2026-05-21: Moldura PhoneFrame APENAS no PC (desktop a simular telemovel).
  // Em device real (Android/iOS/coletor Zebra) NUNCA mostra moldura,
  // mesmo que ?sim=1 esteja na URL (evita "telemovel dentro de telemovel").
  const showFrame = simulator && !realMobile;
  return showFrame ? <PhoneFrame>{content}</PhoneFrame> : content;
}

